/**
 * Lexidy's "e" mark, drawn as pure geometry — a ring with its terminal open
 * to the lower right and the crossbar running past the left edge. It renders
 * in `currentColor`, so the same mark wears brand navy on paper, coral on the
 * dark auth panel, and ink in dark mode, without shipping a bitmap.
 */
export function LexidyMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className={className} aria-hidden="true">
      {/* r=33 ⇒ circumference ≈ 207.3; the 31-unit gap, rotated, is the “e” terminal. */}
      <circle
        cx="54" cy="50" r="33"
        stroke="currentColor" strokeWidth="17"
        strokeDasharray="176 31.4"
        transform="rotate(62 54 50)"
      />
      <rect x="3" y="41.5" width="90" height="17" fill="currentColor" />
    </svg>
  );
}

/* Flat-top hexagon outline centred on (cx,cy) with "radius" r. */
const hexPts = (cx: number, cy: number, r: number) => {
  const h = r * 0.866;
  return `${cx - r},${cy} ${cx - r / 2},${cy - h} ${cx + r / 2},${cy - h} ${cx + r},${cy} ${cx + r / 2},${cy + h} ${cx - r / 2},${cy + h}`;
};

/**
 * The sign-in panel's picture — the legal-tech honeycomb, redrawn in Lexidy's
 * own hand: seven flat-top hexagons, thin white line icons of the craft
 * (courthouse, gavel, briefcase, book, checklist, scales) orbiting the "e"
 * mark in coral at the centre. All geometry, no stock photo.
 */
export function AuthHexScene() {
  const ICON = 'rgba(243,241,234,.82)';
  const HEX = 'rgba(243,241,234,.32)';
  const CORAL = '#d4775c';
  /* Ring centres for r=76 (drawn at 71 so neighbours keep a seam). */
  const ring: [number, number][] = [
    [300, 198], [414, 264], [414, 396], [300, 462], [186, 396], [186, 264],
  ];
  return (
    <svg viewBox="0 0 600 660" fill="none" aria-hidden="true">
      {/* faint scattered cells, the way the reference fades at its edges */}
      {([[86, 140, 26], [520, 120, 20], [548, 330, 26], [80, 470, 20], [488, 540, 24], [140, 590, 16]] as const).map(([x, y, r], i) => (
        <polygon key={i} points={hexPts(x, y, r)} stroke="rgba(243,241,234,.12)" strokeWidth="2" />
      ))}
      {/* the ring */}
      {ring.map(([x, y], i) => (
        <polygon key={i} points={hexPts(x, y, 71)} stroke={HEX} strokeWidth="2.5" />
      ))}
      {/* the centre carries the mark */}
      <polygon points={hexPts(300, 330, 71)} stroke={CORAL} strokeWidth="3" />
      <g transform="translate(300 330)" color={CORAL}>
        <circle cx="2" cy="0" r="17" stroke="currentColor" strokeWidth="8.5" strokeDasharray="90.6 16.2" transform="rotate(62 2 0)" />
        <rect x="-24" y="-4.2" width="47" height="8.5" fill="currentColor" />
      </g>

      <g stroke={ICON} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        {/* gavel — top: tilted mallet head with end caps, handle to the
            lower right, sound block beneath. Unmistakable this time. */}
        <g transform="translate(298 192)">
          <g transform="rotate(-42)">
            <rect x="-16" y="-10" width="32" height="20" rx="5" />
            <path d="M0 10 V30" />
          </g>
          <path d="M-26 27 H4" />
        </g>
        {/* briefcase — upper right */}
        <g transform="translate(414 264)">
          <rect x="-22" y="-10" width="44" height="30" rx="4" />
          <path d="M-8 -10 v-6 a4 4 0 0 1 4 -4 h8 a4 4 0 0 1 4 4 v6 M-22 2 H22" />
        </g>
        {/* checklist — lower right */}
        <g transform="translate(414 396)">
          <rect x="-17" y="-22" width="34" height="44" rx="4" />
          <path d="M-7 -26 h14 M-9 -8 l3 3 6 -7 M2 -6 h8 M-9 6 l3 3 6 -7 M2 8 h8" />
        </g>
        {/* scales — bottom */}
        <g transform="translate(300 462)">
          <path d="M0 -22 V16 M-18 -16 H18 M-14 22 H14" />
          <path d="M-18 -16 l-8 16 M-18 -16 l8 16 M-26 0 a8 8 0 0 0 16 0" />
          <path d="M18 -16 l-8 16 M18 -16 l8 16 M10 0 a8 8 0 0 0 16 0" />
        </g>
        {/* open book — lower left */}
        <g transform="translate(186 396)">
          <path d="M0 -14 C-6 -19 -14 -20 -22 -18 V12 C-14 10 -6 11 0 16 C6 11 14 10 22 12 V-18 C14 -20 6 -19 0 -14 Z" />
          <path d="M0 -14 V16" />
        </g>
        {/* courthouse — upper left */}
        <g transform="translate(186 264)">
          <path d="M-22 -8 L0 -22 L22 -8 Z" />
          <path d="M-15 -2 V14 M-5 -2 V14 M5 -2 V14 M15 -2 V14" />
          <path d="M-20 20 H20" />
        </g>
      </g>
    </svg>
  );
}
